import { useState } from "react";
import StaffingModal from "./StaffingModal";
import { DataTable, ColumnDef } from "../../common/DataTable";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Alert, AlertDescription, AlertTitle } from "../../ui/alert";
import { AlertCircle } from "lucide-react";
import { AspWorkflowStep } from "@/types/staffing/asp";
import { runAspWorkflow } from "@/services/staffing/Staffing";
import { useApiClient } from "@/context/ApiContext";
import { getAspStepLabel, getAspStepStatusBadgeColor } from "@/utils/staffingUtil";
import { describeAspWorkflowError, AspWorkflowErrorInfo } from "@/utils/aspWorkflowError";

interface AspApprovedModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    staffLoginName: string;
    staffName: string | null;
    clientId: number;
    processId?: number;
    initialResults?: AspWorkflowStep[];
}

const columns: ColumnDef<AspWorkflowStep>[] = [
    { key: 'step', header: 'Step', render: (row) => getAspStepLabel(row.step) },
    { key: 'status', header: 'Status', render: (row) => <Badge className={getAspStepStatusBadgeColor(row.status)}>{row.status}</Badge> },
    { key: 'message', header: 'Message' },
    { key: 'detail', header: 'Detail', render: (row) => row.detail ?? '—' },
];

const AspApprovedModal = ({ open, onOpenChange, staffLoginName, staffName, clientId, processId, initialResults }: AspApprovedModalProps) => {
    const apiClient = useApiClient();
    const [running, setRunning] = useState(false);
    const [results, setResults] = useState<AspWorkflowStep[] | null>(initialResults ?? null);
    const [error, setError] = useState<AspWorkflowErrorInfo | null>(null);

    const run = () => {
        setRunning(true);
        setError(null);
        runAspWorkflow({ staffLoginName, clientId, source: 'StaffingDashboard', processId }, apiClient)
            .then(res => setResults(res.data ?? []))
            .catch(err => {
                console.error(err);
                setError(describeAspWorkflowError(err));
            })
            .finally(() => setRunning(false));
    };

    const handleOpenChange = (val: boolean) => {
        if (!val) {
            setRunning(false);
            setResults(initialResults ?? null);
            setError(null);
        }
        onOpenChange(val);
    };

    return (
        <StaffingModal open={open} onOpenChange={handleOpenChange} title={`ASP Approved — ${staffName}`} wide>
            <div className="flex flex-col gap-4">
                {error && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>{error.title}</AlertTitle>
                        <AlertDescription>
                            <p>{error.detail}</p>
                            <p className="mt-1 font-medium">{error.nextStep}</p>
                            {(error.status || error.code || error.traceId) && (
                                <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs opacity-80">
                                    {error.status && (<><dt>Status</dt><dd>{error.status}</dd></>)}
                                    {error.code && (<><dt>Code</dt><dd className="font-mono break-all">{error.code}</dd></>)}
                                    {error.traceId && (<><dt>Trace id</dt><dd className="font-mono break-all">{error.traceId}</dd></>)}
                                </dl>
                            )}
                        </AlertDescription>
                    </Alert>
                )}
                {results === null && (
                    <div className="flex flex-col gap-3">
                        <p className="text-sm text-muted-foreground">
                            This will run the ASP approval workflow for {staffName}. This may take up to 30 seconds.
                        </p>
                        <div>
                            <Button onClick={run} disabled={running}>
                                {running && (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2" />
                                )}
                                {running ? 'Running...' : 'Run'}
                            </Button>
                        </div>
                    </div>
                )}
                {results !== null && (
                    <DataTable data={results} columns={columns} />
                )}
            </div>
        </StaffingModal>
    );
};

export default AspApprovedModal;
